import React, {
  useState,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  useEffect,
} from 'react';
import { ImagePlus, Icosahedron } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { Content, ImageToCadModel, Model } from '@shared/types';
import { shouldShowPolygonControls } from '@/constants/meshConstants';

// Local helper functions for this component
const shouldShowQuadsControls = (model: Model): boolean => {
  return shouldShowPolygonControls(model as ImageToCadModel);
};

// True only when a drag operation actually carries external files. Internal
// element drags (e.g. reordering items inside the feature tree) expose no
// 'Files' entry in dataTransfer.types, so this gate stops them from
// false-triggering the file dropzone.
const dragCarriesFiles = (e: DragEvent): boolean =>
  !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
import { MessageItem } from '../types/misc.ts';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AskTomInput } from '@/components/chat/AskTomInput';
import { GenerationGateModal } from '@/components/GenerationGateModal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { ModelSelector } from '@/components/ModelSelector';
import {
  OutputFormatSelector,
  OutputFormat,
} from '@/components/OutputFormatSelector';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useItemSelection } from '@/hooks/useItemSelection';
// Type-only: a value import of meshUtils pulls three + STL/OBJ loaders onto
// the LANDING page (A2 Lazy3D leak) — the fns are dynamic-imported on upload.
import type { BoundingBox } from '@/utils/meshUtils';
import { useMeshFiles } from '@/contexts/mesh-files-context';
import { QuadsButton, PolygonButton } from './textAreaChat/PolygonControls';
import {
  SUPPORTED_MESH_EXTENSIONS,
  VALID_IMAGE_FORMATS,
  getMeshFileType,
  isSupportedMeshFile,
} from './textAreaChat/fileTypes';
import { AttachmentPreview } from './textAreaChat/AttachmentPreview';
import { readStoredMeshTopology } from './textAreaChat/persistence';
import { useOutputFormat } from './textAreaChat/useOutputFormat';
import { usePolygonControls } from './textAreaChat/usePolygonControls';
import { usePlaceholderAnimation } from './textAreaChat/usePlaceholderAnimation';
import { useImageModelLock } from './textAreaChat/useImageModelLock';
import { useAttachmentUploads } from './textAreaChat/useAttachmentUploads';
import {
  useHostHandles,
  type HostHandleProps,
} from './textAreaChat/useHostHandles';

interface TextAreaChatProps extends HostHandleProps {
  type: 'parametric' | 'imagetocad';
  onSubmit: (content: Content) => void;
  onFocus?: () => void;
  isLoading?: boolean;
  placeholder?: string;
  stopGenerating?: () => void;
  disabled?: boolean;
  model: Model;
  setModel: (model: Model) => void;
  showPromptGenerator?: boolean;
  showFullLabels?: boolean; // Controls whether to show full text labels on buttons
  onTypeChange?: (type: 'parametric' | 'imagetocad') => void;
  conversation: {
    id: string;
    user_id: string;
  };
  initialOutputFormat?: OutputFormat;
  onOutputFormatChange?: (format: OutputFormat) => void;
  requiresSolidbody?: boolean;
  vehicleParamsReady?: boolean;
  onHasTextChange?: (hasText: boolean) => void;
  externalEnabled?: boolean;
  /** Ask Tom Q&A inline rendering. When BOTH are provided AND the
   *  conversation is parametric, the Ask Tom row is rendered inside this
   *  TextAreaChat box (between the prompt+send row and the upload-button
   *  section). The parent owns the qaHidden state since ChatSection
   *  also needs it for filtering the message list — single source of
   *  truth, no localStorage round-trip. */
  qaHidden?: boolean;
  onQaHiddenChange?: (hidden: boolean) => void;
  simplified?: boolean;
}

function TextAreaChat({
  onSubmit,
  onFocus,
  isLoading = false,
  placeholder = 'What can Telekinesis help you build today?',
  type,
  stopGenerating,
  disabled = false,
  model,
  setModel,
  showFullLabels = false,
  conversation,
  initialOutputFormat,
  onOutputFormatChange,
  requiresSolidbody,
  vehicleParamsReady = false,
  onHasTextChange,
  qaHidden,
  onQaHiddenChange,
  externalEnabled = false,
  onRegisterSubmit,
  onRegisterAttach,
  onRegisterSetText,
  hideAttachRow = false,
  simplified = false,
}: TextAreaChatProps) {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState('');

  useEffect(() => {
    onHasTextChange?.(!!input?.trim());
  }, [input, onHasTextChange]);

  // The drag-to-drop file overlay ("Drop images and 3D models here") is only
  // offered for creative chats. Parametric chats upload via the click/paste
  // buttons only — no dropzone overlay, no show-dropzone drag wiring.
  const dropzoneEnabled = type !== 'parametric';

  const [isDragging, setIsDragging] = useState(false);
  const [isDragHover, setIsDragHover] = useState(false);
  const [dropMessageOpacityClass, setDropMessageOpacityClass] = useState(
    'opacity-0 pointer-events-none',
  );
  const [dropMessageTransitionClass, setDropMessageTransitionClass] =
    useState('');
  const prevIsDraggingRef = useRef(isDragging);
  const { toast } = useToast();
  const { session, user } = useAuth();
  const { images, mesh, setImages, setMesh } = useItemSelection();

  // Generation gating (#176): the host passes `disabled` = limitReached (a
  // confirmed zero-credit balance). Rather than leave the send button
  // silently dead, open the gate modal — guests get "sign up", signed-in
  // users get "upgrade". We only key the gate off the credit/guest block;
  // the params/empty-prompt disable below is unchanged.
  const isAnonymous = !user || user.is_anonymous;
  const [gateOpen, setGateOpen] = useState(false);
  const meshFiles = useMeshFiles();

  // Parametric mode: bounding box and filename from STL parsing
  const [meshBoundingBox, setMeshBoundingBox] = useState<BoundingBox | null>(
    null,
  );
  const [meshFilename, setMeshFilename] = useState<string | null>(null);

  // Quads vs Polys toggle state (only for ultra model)
  const [meshTopology, setMeshTopology] = useState<'quads' | 'polys'>(() =>
    readStoredMeshTopology(),
  );

  // Kernel selection (output format) state + sync effects — see useOutputFormat.
  const { outputFormat, setOutputFormat } = useOutputFormat({
    initialOutputFormat,
    conversationId: conversation.id,
    requiresSolidbody,
    onOutputFormatChange,
  });

  // Polygon-count / mesh-topology derived state, callbacks, persistence and
  // reset logic — see usePolygonControls. meshTopology stays owned here.
  const {
    setPolygonCountForCurrentModel,
    handleMeshTopologyChange,
    polygonCount,
    resetPolygonCount,
    maxPolygonCount,
  } = usePolygonControls({ type, model, meshTopology, setMeshTopology });

  // Refs for the two hot-zones
  const topDropZoneRef = useRef<HTMLDivElement>(null);
  const textAreaContainerZoneRef = useRef<HTMLDivElement>(null);

  // Placeholder typed-out crossfade animation — see usePlaceholderAnimation.
  const { placeholderAnim, placeholderOpacity } = usePlaceholderAnimation({
    type,
    imagesLength: images.length,
    placeholder,
    model,
  });

  // Attaching an image makes generation exclusive to the image tiers: lock the
  // picker to the image tiers, grey out the rest, default to Magneto 0.1.
  const { models: imageAwareModels } = useImageModelLock(
    type,
    images,
    model,
    setModel,
  );

  // Intentionally a fresh closure each render so the submit handler published
  // to the host (useHostHandles) always reflects current state. Wrapping this
  // large async handler in useCallback would risk stale closures for marginal
  // gain (the registration effect is cheap).
  const handleSubmit = async () => {
    const hasNoContent =
      images.length === 0 && !input?.trim() && !mesh && !vehicleParamsReady;
    const hasUploadingImages = images.some((img) => img.isUploading);

    if (hasNoContent || isLoading || hasUploadingImages) {
      return;
    }
    const resolvedText =
      input.trim() !== ''
        ? input.trim()
        : vehicleParamsReady
          ? 'Design a converging-diverging de Laval rocket nozzle'
          : '';
    let content: Content = {
      ...(resolvedText !== '' && { text: resolvedText }),
      ...(images.length > 0 && { images: images.map((img) => img.id) }),
      model: model,
    };
    if (type === 'imagetocad') {
      content = {
        ...content,
        ...(mesh && {
          mesh: { id: mesh.id, fileType: mesh.fileType || 'glb' },
        }),
        // Include meshTopology preference for standard and ultra models
        ...(shouldShowPolygonControls(model as ImageToCadModel) && {
          meshTopology,
        }),
        // Include polygonCount preference for standard and ultra (respect quads mode limit)
        ...(shouldShowPolygonControls(model as ImageToCadModel) && {
          polygonCount: Math.min(polygonCount, maxPolygonCount),
        }),
      };
    } else if (type === 'parametric' && mesh) {
      content = {
        ...content,
        mesh: { id: mesh.id, fileType: 'stl' },
        ...(meshBoundingBox && { meshBoundingBox }),
        ...(meshFilename && { meshFilename }),
      };
    }
    // Attach the user's kernel choice so PromptView can persist it (only
    // matters for the first message of a new conversation; downstream
    // routing prefers conversation.settings → localStorage cache).
    onSubmit({ ...content, outputFormat });
    setInput('');
    setImages([]);
    setMesh(null);
    setMeshBoundingBox(null);
    setMeshFilename(null);
  };

  // Image + mesh upload mutations — see useAttachmentUploads. `addItems` and
  // the drag/paste/drop ingest flow stay in this root (tightly coupled to the
  // attachment state setters + the STL-parse/multi-angle-render pipeline).
  const { uploadImageAsync, uploadMeshAsync } = useAttachmentUploads({
    conversation,
    toast,
  });

  const addItems = async (files: FileList) => {
    const newItems = Array.from(files);
    let hasSmallImages = false;
    let hasLargeImages = false;
    let hasInvalidImages = false;
    let hasInvalidItems = false;
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB limit

    const validImages = await Promise.all(
      newItems.map(async (file) => {
        // First check file type Must be jpeg, png, gif, or webp.
        if (!file.type.includes('image')) {
          return null;
        }

        if (!VALID_IMAGE_FORMATS.includes(file.type)) {
          hasInvalidImages = true;
          return null;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          hasLargeImages = true;
          return null;
        }

        // Check dimensions asynchronously
        return new Promise<File | null>((resolve) => {
          const img = new Image();
          img.src = URL.createObjectURL(file);
          img.onload = () => {
            if (img.naturalWidth < 256 || img.naturalHeight < 256) {
              hasSmallImages = true;
              resolve(null); // Image too small
            } else {
              resolve(file); // Valid image
            }
            URL.revokeObjectURL(img.src);
          };
          img.onerror = () => {
            resolve(null); // Invalid image
            URL.revokeObjectURL(img.src);
          };
        });
      }),
    );

    const validMeshes = newItems.map((file) => {
      if (!isSupportedMeshFile(file.name, type)) {
        return null;
      }

      return file;
    });

    // Filter out null values (invalid images)
    const filteredImages = validImages.filter(
      (img): img is File => img !== null,
    );

    const filteredMeshes = validMeshes.filter(
      (mesh): mesh is File => mesh !== null,
    );

    hasInvalidItems =
      newItems.length > filteredImages.length + filteredMeshes.length;

    // Show specific errors first, then generic error only if there are truly invalid file types
    if (hasSmallImages) {
      toast({
        title: 'Image too small',
        description:
          'Some images were not added because they are smaller than 256x256 pixels.',
      });
    } else if (hasLargeImages) {
      toast({
        title: 'Image too large',
        description:
          'Some images were not added because they are larger than 100MB.',
      });
    } else if (hasInvalidImages) {
      toast({
        title: 'Invalid image format',
        description:
          'Some images were not added because they are not valid image formats. Must be jpeg, png, or webp.',
      });
    } else if (hasInvalidItems) {
      toast({
        title: 'Invalid file format',
        description:
          type === 'imagetocad'
            ? 'Some files were not added because they are not valid file formats. Must be jpeg, png, webp, glb, stl, or obj.'
            : 'Some files were not added because they are not valid file formats. Must be jpeg, png, webp, or stl.',
      });
    }

    filteredMeshes.forEach(async (file) => {
      const tempId = crypto.randomUUID();
      const fileType = getMeshFileType(file.name);
      setMesh({ id: tempId, isUploading: true, source: 'upload', fileType });
      try {
        // For parametric mode STL files, extract bounding box and generate multi-angle renders
        if (type === 'parametric' && fileType === 'stl') {
          // Dynamic import: keeps three/loaders off the landing chunk; only
          // runs on user upload (viewer chunk usually idle-prefetched by then).
          const { parseSTL, renderMultipleAngles } = await import(
            '@/utils/meshUtils'
          );
          const { geometry, boundingBox } = await parseSTL(file);
          setMeshBoundingBox(boundingBox);
          setMeshFilename(file.name);

          // Store STL blob in context for WASM filesystem access
          meshFiles.setMeshFile(file.name, file);

          // Generate multi-angle renders and upload as images
          const renders = await renderMultipleAngles(geometry, boundingBox);
          for (const renderBlob of renders) {
            const renderId = crypto.randomUUID();
            const renderFile = new File(
              [renderBlob],
              `render-${renderId}.png`,
              {
                type: 'image/png',
              },
            );
            const url = URL.createObjectURL(renderBlob);
            setImages((prevImages) => [
              ...prevImages,
              { id: renderId, isUploading: true, source: 'upload', url },
            ]);
            try {
              const signedUrl = await uploadImageAsync({
                file: renderFile,
                id: renderId,
              });
              URL.revokeObjectURL(url);
              setImages((prevImages) =>
                prevImages.map((img) =>
                  img.id === renderId
                    ? { ...img, isUploading: false, url: signedUrl }
                    : img,
                ),
              );
            } catch (renderError) {
              console.error('Error uploading render:', renderError);
              setImages((prevImages) =>
                prevImages.filter((img) => img.id !== renderId),
              );
            }
          }

          geometry.dispose();
        }

        const url = await uploadMeshAsync({ file: file, id: tempId });
        setMesh({
          id: tempId,
          isUploading: false,
          url,
          source: 'upload',
          fileType,
        });
      } catch (error) {
        console.error('Error uploading mesh:', error);
        setMesh(null);
        setMeshBoundingBox(null);
        setMeshFilename(null);
      }
    });

    // Upload each valid image immediately
    filteredImages.forEach(async (file) => {
      const tempId = crypto.randomUUID();
      const url = URL.createObjectURL(file);
      setImages((prevImages) => [
        ...prevImages,
        { id: tempId, isUploading: true, source: 'upload', url },
      ]);
      try {
        const signedUrl = await uploadImageAsync({ file, id: tempId });
        URL.revokeObjectURL(url);
        setImages((prevImages) =>
          prevImages.map((img) =>
            img.id === tempId
              ? { ...img, isUploading: false, url: signedUrl }
              : img,
          ),
        );
      } catch (error) {
        console.error('Error uploading image:', error);
        setImages((prevImages) =>
          prevImages.filter((img) => img.id !== tempId),
        );
      }
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = event.clipboardData.files;
    if (files && files.length > 0) {
      event.preventDefault();
      addItems(files);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Signal that this component handled the drop
    const droppedFiles = event.dataTransfer?.files;

    let shouldAddItems = false;
    const target = event.target as Node;

    if (
      topDropZoneRef.current?.contains(target) ||
      textAreaContainerZoneRef.current?.contains(target)
    ) {
      shouldAddItems = true;
    }

    if (shouldAddItems && droppedFiles && droppedFiles.length > 0) {
      await addItems(droppedFiles);
    }

    // Always reset drag states, regardless of whether files were added
    setIsDragging(false);
    setIsDragHover(false);
  };

  const handleItemsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedItems = event.target.files;
    if (selectedItems && selectedItems.length > 0) {
      addItems(selectedItems);
    }
  };

  const handleMeshRemoved = async () => {
    if (mesh?.source === 'upload') {
      try {
        const fileExtension = mesh.fileType || 'glb'; // Default to glb if fileType is not set
        await Promise.all([
          supabase.storage
            .from('meshes')
            .remove([
              `${session?.user?.id}/${conversation.id}/${mesh.id}.${fileExtension}`,
            ]),
          supabase.storage
            .from('images')
            .remove([
              `${session?.user?.id}/${conversation.id}/preview-${mesh.id}`,
            ]),
        ]);
      } catch (error) {
        console.error('Error removing mesh:', error);
      }
    }
    setMesh(null);
  };

  const handleImageRemoved = async (image: MessageItem) => {
    if (!image.isUploading) {
      // Only try to remove from storage if the item has been uploaded
      if (image.source === 'upload') {
        try {
          await supabase.storage
            .from('images')
            .remove([`${session?.user?.id}/${conversation.id}/${image.id}`]);
        } catch (error) {
          console.error('Error removing image:', error);
        }
      }
      setImages((prevImages) =>
        prevImages.filter((img) => img.id !== image.id),
      );
    }
  };

  // Add global drag-and-drop listeners so that dropping files anywhere on the page is handled.
  useEffect(() => {
    // The dropzone overlay only exists for non-parametric (creative) chats.
    // For parametric we skip the show-dropzone wiring entirely.
    if (!dropzoneEnabled) return;

    // Prevent default browser behaviour (e.g. opening the image in a new tab)
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDragEnter = (e: DragEvent) => {
      // Only a real EXTERNAL FILE drag should reveal the dropzone. An internal
      // element drag (e.g. dragging items inside the feature tree) carries no
      // 'Files' type and must NOT trigger it.
      if (!dragCarriesFiles(e)) return;
      e.preventDefault();
      setIsDragging(true);
      // When a drag operation newly enters the window, assume it's not hovering
      // over a specific component's hot-zone yet. Hot-zones will override this.
      setIsDragHover(false);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      // When leaving the window entirely (relatedTarget is null), reset dragging state
      if (
        (e as unknown as { relatedTarget: Node | null }).relatedTarget === null
      ) {
        setIsDragging(false);
        setIsDragHover(false);
      }
    };

    const handleDropGlobal = async (e: DragEvent) => {
      // Always reset drag state — the drag operation ended regardless of who handled the drop.
      setIsDragging(false);
      setIsDragHover(false);

      // Only prevent browser default (e.g. opening the file) when no other handler claimed it.
      if (!e.defaultPrevented) {
        e.preventDefault();
      }
      // Never call addItems() here — only the component's own onDrop handler does that.
    };

    const handleDragEnd = () => {
      // Drag cancelled (e.g. Escape key) — no drop event fires, so clean up manually.
      setIsDragging(false);
      setIsDragHover(false);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDropGlobal);
    window.addEventListener('dragend', handleDragEnd);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDropGlobal);
      window.removeEventListener('dragend', handleDragEnd);
    };
  }, [dropzoneEnabled]);

  useEffect(() => {
    if (images.length === 0 && mesh === null) {
      // Case 1: No items are present in the drop zone
      if (isDragging) {
        // If dragging, the message should be visible and can transition
        setDropMessageOpacityClass('opacity-100');
        setDropMessageTransitionClass(
          'transition-opacity duration-200 ease-in-out',
        );
      } else {
        // Not dragging. Message should be hidden.
        if (prevIsDraggingRef.current) {
          // If it WAS dragging and now it's not, it should fade out.
          setDropMessageTransitionClass(
            'transition-opacity duration-200 ease-in-out',
          );
          setDropMessageOpacityClass('opacity-0 pointer-events-none');
        } else {
          // If it was NOT dragging and still isn't (e.g., mounting fresh after image removal),
          // it should be instantly hidden, no transition.
          setDropMessageTransitionClass('');
          setDropMessageOpacityClass('opacity-0 pointer-events-none');
        }
      }
    } else {
      // Case 2: Items ARE present in the drop zone, message should be instantly hidden.
      setDropMessageTransitionClass('');
      setDropMessageOpacityClass('opacity-0 pointer-events-none');
    }
    prevIsDraggingRef.current = isDragging;
  }, [isDragging, images.length, mesh]); // Listen to images.length and mesh too

  // Reasons the button is dead that are NOT the credit/guest gate: empty
  // prompt, in-flight image upload. These keep their normal disabled state.
  const nonGateDisabled =
    (images.length === 0 &&
      !input?.trim() &&
      !vehicleParamsReady &&
      !externalEnabled) ||
    images.some((img) => img.isUploading);

  // The credit/guest gate (#176): the host's `disabled` prop carries
  // limitReached. When that's the only blocker we keep the button CLICKABLE
  // and open the gate modal instead of submitting.
  const gateBlocked = disabled && !nonGateDisabled;

  const isSubmitDisabled = nonGateDisabled || disabled;

  // Imperative handles for hosts that render their own chrome (sidebar
  // Generate button; the centred prompt card's upload well and "Try" row).
  useHostHandles({
    onRegisterSubmit,
    onRegisterAttach,
    onRegisterSetText,
    handleSubmit,
    submitEnabled: !isSubmitDisabled,
    isLoading,
    stopGenerating,
    addItems,
    setText: (text) => {
      setInput(text);
      textareaRef.current?.focus();
    },
  });

  return (
    <div
      className="group relative"
      onDrop={handleDrop}
      onDragEnter={(event) => {
        // Parametric has no dropzone; and only a real external file drag (not
        // an internal element drag) should reveal it for creative chats.
        if (!dropzoneEnabled || !dragCarriesFiles(event.nativeEvent)) return;
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(event) => {
        if (!dropzoneEnabled || !dragCarriesFiles(event.nativeEvent)) return;
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        if (!dropzoneEnabled) return;
        event.preventDefault();
        // If the drag operation leaves the bounds of this entire component,
        // then isDragHover should definitely be false.
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsDragHover(false);
        }
      }}
      onClick={(event) => {
        // This wrapper spans the whole prompt block, including the
        // AskTomInput question field and the send/upload button rows.
        // Clicking the empty padding should focus the main textarea as
        // a convenience — but if the click landed on a real focusable
        // control we must NOT steal focus back to the textarea. That was
        // the "Questions don't work in chat" bug: clicking the Ask Tom
        // field focused it for one frame, then this handler's
        // textareaRef.focus() yanked the caret up to the iterate prompt.
        const target = event.target as HTMLElement;
        if (
          target.closest(
            'input, textarea, button, select, a, [contenteditable="true"]',
          )
        ) {
          return;
        }
        onFocus?.();
        textareaRef.current?.focus();
      }}
    >
      <div
        ref={topDropZoneRef}
        className={cn(
          'mx-auto flex w-[95%] min-w-52 overflow-hidden rounded-t-xl border-x-2 border-t-2',
          'transition-[height,opacity,border-color,background-color] duration-200 ease-in-out',
          disabled
            ? 'h-0 border-transparent bg-transparent opacity-0'
            : !isDragging && images.length === 0 && mesh === null
              ? 'h-0 border-transparent bg-transparent opacity-0'
              : isDragging
                ? isDragHover
                  ? 'h-20 border-[#FFBA28] bg-[rgba(255,186,40,0.24)] opacity-100' // Blue, full height
                  : 'h-20 border-[#D4A000] bg-[rgba(255,186,40,0.12)] opacity-100' // Intermediate blue, full height
                : images.length > 0 || mesh !== null
                  ? 'h-20 border-telekinesis-neutral-700 bg-telekinesis-neutral-950 opacity-100'
                  : 'h-0 border-transparent bg-transparent opacity-0',
        )}
        onDragEnter={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragOver={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragLeave={(event) => {
          if (isDragging) {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setIsDragHover(false);
            }
          }
        }}
      >
        <AttachmentPreview
          disabled={disabled}
          showDropMessage={dropzoneEnabled}
          isDragging={isDragging}
          isDragHover={isDragHover}
          images={images}
          mesh={mesh}
          dropMessageTransitionClass={dropMessageTransitionClass}
          dropMessageOpacityClass={dropMessageOpacityClass}
          onMeshRemoved={handleMeshRemoved}
          onImageRemoved={handleImageRemoved}
        />
      </div>
      <div
        ref={textAreaContainerZoneRef}
        className={cn(
          'relative rounded-2xl border-2',
          isFocused
            ? 'border-telekinesis-gold shadow-[inset_0px_0px_8px_0px_rgba(0,0,0,0.08)]'
            : 'border-telekinesis-neutral-700 shadow-[inset_0px_0px_8px_0px_rgba(0,0,0,0.08)] hover:border-telekinesis-neutral-400',
          'bg-telekinesis-background-2 transition-all duration-300',
        )}
        onDragEnter={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragOver={(event) => {
          if (isDragging) {
            event.preventDefault();
            setIsDragHover(true);
          }
        }}
        onDragLeave={(event) => {
          if (isDragging) {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setIsDragHover(false);
            }
          }
        }}
      >
        <div className="flex select-none items-center justify-between p-2">
          <div className="relative grid w-full">
            <Tooltip>
              <TooltipTrigger asChild>
                <Textarea
                  disabled={isLoading || disabled}
                  value={input}
                  ref={textareaRef}
                  translate="no"
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onBlur={() => setIsFocused(false)}
                  onFocus={() => setIsFocused(true)}
                  onChange={(e) => {
                    setInput(e.target.value);
                  }}
                  placeholder={placeholderAnim}
                  className="hide-scrollbar z-40 block h-auto min-h-0 w-full resize-none overflow-hidden whitespace-pre-line break-words border-none bg-telekinesis-neutral-800 bg-transparent px-3 py-2 text-base text-telekinesis-text-primary outline-none transition-all duration-500 placeholder:text-telekinesis-text-secondary placeholder:opacity-[var(--placeholder-opacity)] placeholder:transition-all placeholder:duration-300 placeholder:ease-in-out hover:placeholder:blur-[0.2px] focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-gray-200 sm:px-4 sm:text-sm"
                  style={
                    {
                      '--placeholder-opacity': placeholderOpacity,
                      gridArea: '1 / -1',
                    } as React.CSSProperties
                  }
                  rows={1}
                />
              </TooltipTrigger>
              <TooltipContent>
                Text Prompt. (Recommended for individual parts. Optional for
                Vehicles.)
              </TooltipContent>
            </Tooltip>
            <div
              className="pointer-events-none col-start-1 row-start-1 w-full overflow-hidden whitespace-pre-wrap break-words px-3 py-2 text-base opacity-0 sm:px-4 sm:text-sm"
              style={{ gridArea: '1 / -1' }}
            >
              <span>{input || placeholderAnim}</span>
              <br />
            </div>
          </div>
          {!simplified &&
            (isLoading && stopGenerating ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={stopGenerating}
                    className="flex h-8 w-8 transform items-center justify-center rounded-lg bg-telekinesis-neutral-700 p-1 text-white transition-all duration-300 hover:scale-105 hover:bg-telekinesis-gold/90 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:bg-telekinesis-gold"
                  >
                    <span className="h-3.5 w-3.5 rounded-[2px] bg-black" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Stop generation</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* When blocked by credits/guest (#176) the button stays
                      CLICKABLE and opens the gate modal instead of submitting.
                      The params/empty-prompt disable (nonGateDisabled) is
                      unchanged. */}
                  <button
                    onClick={
                      gateBlocked ? () => setGateOpen(true) : handleSubmit
                    }
                    className={cn(
                      // Yellow primary CTA — telekinesis-yellow (#FFBA28),
                      // black arrow for contrast. Hover bumps to a glowing
                      // halo via box-shadow so the button feels active
                      // without changing position.
                      'flex h-8 w-8 transform items-center justify-center rounded-lg bg-[#FFBA28] p-1 text-black transition-all duration-300 hover:scale-105 hover:bg-[#FFBA28] hover:shadow-[0_0_18px_rgba(255,186,40,0.7)] disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-none',
                      images.some((img) => img.isUploading) && 'opacity-50',
                    )}
                    disabled={gateBlocked ? false : isSubmitDisabled}
                  >
                    <Icosahedron className="h-2.5 w-2.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {gateBlocked
                    ? isAnonymous
                      ? 'Sign up to generate.'
                      : 'Out of credits — upgrade to generate.'
                    : isSubmitDisabled
                      ? 'Change Parameters or enter Text Prompt to enable new generation.'
                      : 'Send it.'}
                </TooltipContent>
              </Tooltip>
            ))}
        </div>
        {/* Ask Tom Q&A row. Lives INSIDE the prompt outer-box (the
            rounded-2xl border-2 above) so it visually belongs to the
            chat input — sits between the prompt+send line and the
            upload-button row below. Mounts for BOTH parametric and
            image-to-CAD chats (Tom is the only chat surface for asking
            questions) whenever the host wires the qa state through. */}
        {qaHidden !== undefined && onQaHiddenChange && conversation && (
          <AskTomInput
            conversationId={conversation.id}
            model={model as Model}
            hidden={qaHidden}
            onHiddenChange={onQaHiddenChange}
          />
        )}
        {type === 'parametric' && !hideAttachRow && (
          <div className="flex items-center border-t border-[#2a2a2a] p-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="flex h-8 w-auto items-center gap-2 rounded-lg border border-[#2a2a2a] bg-telekinesis-background-2 px-2 text-sm text-telekinesis-text-secondary hover:bg-telekinesis-bg-secondary-dark"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = `${VALID_IMAGE_FORMATS.join(', ')}, .stl`;
                    input.onchange = (event) => {
                      handleItemsChange(
                        event as unknown as ChangeEvent<HTMLInputElement>,
                      );
                    };
                    input.click();
                  }}
                  disabled={disabled}
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-xs">Add Image/Schematic</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Only .JPG, .PNG, .WEBP, .STL files accepted
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        {!simplified && type !== 'parametric' && (
          <div className="flex items-center justify-between border-t border-[#2a2a2a] p-3">
            <div className="flex items-center gap-1">
              <ModelSelector
                disabled={isLoading || disabled}
                models={imageAwareModels}
                selectedModel={model}
                onModelChange={setModel}
                type={type}
                focused={isFocused}
                useShortLabel={!showFullLabels}
              />
              <OutputFormatSelector
                selectedFormat={outputFormat}
                onFormatChange={setOutputFormat}
                disabled={isLoading || disabled || !!requiresSolidbody}
                focused={isFocused}
                useShortLabel={!showFullLabels}
                lockedTooltip={
                  requiresSolidbody
                    ? 'Solidbody Physics Required for Python-scripted Structures'
                    : undefined
                }
              />

              {/* Quads vs Polys toggle button - show for standard and ultra models */}
              {type === 'imagetocad' && shouldShowQuadsControls(model) && (
                <QuadsButton
                  meshTopology={meshTopology}
                  showFullLabels={showFullLabels}
                  isLoading={isLoading}
                  disabled={disabled}
                  onToggle={() =>
                    handleMeshTopologyChange(
                      meshTopology === 'quads' ? 'polys' : 'quads',
                    )
                  }
                />
              )}

              {/* Polygon Count button - show for standard and ultra models */}
              {type === 'imagetocad' && shouldShowQuadsControls(model) && (
                <PolygonButton
                  polygonCount={polygonCount}
                  meshTopology={meshTopology}
                  model={model}
                  showFullLabels={showFullLabels}
                  isLoading={isLoading}
                  disabled={disabled || false}
                  onPolygonCountChange={setPolygonCountForCurrentModel}
                  onReset={resetPolygonCount}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex h-8 w-auto items-center gap-2 rounded-lg border border-[#2a2a2a] bg-telekinesis-background-2 px-2 text-sm text-telekinesis-text-secondary hover:bg-telekinesis-bg-secondary-dark"
                    onClick={(e) => {
                      e.stopPropagation();
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = `${VALID_IMAGE_FORMATS.join(', ')}, ${
                        type === 'imagetocad'
                          ? SUPPORTED_MESH_EXTENSIONS.join(', ')
                          : '.stl'
                      }`;
                      input.onchange = (event) => {
                        handleItemsChange(
                          event as unknown as ChangeEvent<HTMLInputElement>,
                        );
                      };
                      input.click();
                    }}
                    disabled={disabled}
                  >
                    <ImagePlus className="h-5 w-5" />
                    {showFullLabels && (
                      <span className="text-xs">Add Image</span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Only .JPG, .PNG, .WEBP files accepted
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      {/* Generation gate (#176) — sign-up (guest) or upgrade (no credits)
          when the send button is blocked by a zero-credit balance. */}
      <GenerationGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        mode={isAnonymous ? 'guest' : 'no-credits'}
      />
    </div>
  );
}

export default TextAreaChat;
